import { useState } from "react";
import BuyBoxChip from "./BuyBoxChip";

// ─── BuyBoxOverride ─────────────────────────────────────────────────────────
// Inline three-state selector rendered inside the deal page "In Buy Box?"
// FactRow. Wraps BuyBoxChip and lets the user force a verdict without
// touching the rule.
//
// States:
//   Auto     — evaluator decides (verdict comes from the current rule).
//              Sending Auto also NULLs the manual reason on save so a
//              later re-derive can rewrite cleanly.
//   Yes      — force verdict='in_box',   source='manual'
//   No       — force verdict='filtered', source='manual', reason='Manual override'
//
// Props:
//   verdict, source, reason — current buy-box state (from the deal row)
//   onOverride({ source, verdict, reason }) — called with the payload that
//     should be merged into the updateDeal() call. The parent is responsible
//     for actually persisting.
//   saving — bool, greys out controls during the round-trip

const OPTIONS = [
  { key: "auto", label: "Auto",     hint: "Let the buy-box rule decide" },
  { key: "yes",  label: "Yes",      hint: "Force In-Box (manual override)" },
  { key: "no",   label: "No",       hint: "Force Filtered (manual override)" },
];

function currentKey(verdict, source) {
  if (source !== "manual") return "auto";
  if (verdict === "in_box") return "yes";
  if (verdict === "filtered") return "no";
  return "auto";
}

export default function BuyBoxOverride({ verdict, source, reason, onOverride, saving = false }) {
  const [open, setOpen] = useState(false);
  const active = currentKey(verdict, source);

  function pick(key) {
    setOpen(false);
    if (key === active) return;
    if (key === "auto") {
      onOverride?.({ buyBoxSource: "auto", buyBoxVerdict: null, buyBoxReason: null });
    } else if (key === "yes") {
      onOverride?.({ buyBoxSource: "manual", buyBoxVerdict: "in_box", buyBoxReason: null });
    } else {
      onOverride?.({ buyBoxSource: "manual", buyBoxVerdict: "filtered", buyBoxReason: "Manual override" });
    }
  }

  // Show the chip (or a tiny "—" placeholder when undecided), plus a small
  // pencil-style trigger. The trigger opens a compact popover with three
  // radio-style rows. Not a native <select> so we can keep the chip in view.
  return (
    <span className="relative inline-flex items-center gap-2 justify-end">
      {verdict && verdict !== "undecided"
        ? <BuyBoxChip verdict={verdict} source={source} reason={reason} size="md" />
        : <span className="text-xs text-[#8a8a8a]">— undecided</span>}
      <button
        type="button"
        disabled={saving}
        onClick={() => setOpen(o => !o)}
        className="text-[10px] font-semibold px-2 py-0.5 rounded-full border border-[#4a4a4a] text-[#8a8a8a] hover:text-[#f4f4f4] hover:border-[#5a5a5a] disabled:opacity-40"
        title="Override or reset to auto"
      >
        {active === "auto" ? "Override ▾" : `${OPTIONS.find(o => o.key === active).label} ▾`}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            className="absolute right-0 top-full mt-1 z-50 bg-[#2c2c2c] border border-[#3a3a3a] rounded-lg shadow-xl py-1 w-52"
            onClick={e => e.stopPropagation()}
          >
            {OPTIONS.map(o => (
              <button
                key={o.key}
                onClick={() => pick(o.key)}
                className={`w-full text-left px-3 py-1.5 hover:bg-[#3a3a3a] transition-colors ${
                  o.key === active ? "text-pp-red font-semibold" : "text-[#b4b4b4]"
                }`}
              >
                <div className="text-xs">{o.label}</div>
                <div className="text-[10px] text-[#8a8a8a]">{o.hint}</div>
              </button>
            ))}
          </div>
        </>
      )}
    </span>
  );
}
